import { Component, DestroyRef, Inject } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Record } from 'src/models/ts/collection-form-field.model';
import { ItemTemplateDirective, MultiSelectComponent, TagTemplateDirective } from '@progress/kendo-angular-dropdowns';
import { FormsModule } from '@angular/forms';
import { ModalComponent } from '../../../../../../../../shared/components/modals/modal/modal.component';
import { TranslatePipe } from '../../../../../../../../shared/pipes/translate/translate.pipe';
import { map, take, tap, zip } from 'rxjs';
import { CollectionListApiService } from '../../../../../../../../api/bizzmine/collection-list/collection-list-api.service';
import { Store } from '@ngrx/store';
import { userSettingsFeature } from '../../../../../../../../store/features/user-settings/user-settings-feature';
import { formsActions } from '../../../../../../../../store/features/forms/forms-actions';
import { ProtectedFieldType } from '../../../../../../../../../models/ts/protected-field-type.model';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CollectionFieldListOptionsConfiguration } from '../../../../../../../../../models/ts/collection-form-field-value.model';
import { ToggleArrowComponent } from '../../../../../../../../shared/components/ui/toggle-arrow/toggle-arrow.component';
import { ChecklistApiService } from 'src/app/api/bizzmine/checklist/checklist.service';

@Component({
  selector: 'bizz-question-bulk-group',
  standalone: true,
  imports: [
    FormsModule,
    ModalComponent,
    TranslatePipe,
    ItemTemplateDirective,
    MultiSelectComponent,
    TagTemplateDirective,
    ToggleArrowComponent
  ],
  templateUrl: './question-bulk-group.component.html',
  styleUrl: './question-bulk-group.component.scss'
})
export class QuestionBulkGroupComponent {
  private userLanguage = this.store$.selectSignal(userSettingsFeature.selectUserLanguagesID)();
  private systemLanguage = this.store$.selectSignal(userSettingsFeature.selectCompanyLanguagesID)();
  public selected: Array<{ Text: string, Value: number }> | undefined = undefined;
  public filteredOptions: Array<{ Text: string, Value: number }>;
  public options: Array<{ Text: string, Value: number }>;
  public groupListOptions: Array<CollectionFieldListOptionsConfiguration>;
  public canCopyFromCategory = false;
  public copyFromCategory = false;
  public collectionFieldListOptionsConfiguration$ = this.collectionListApiService.getCollectionFieldListOptionsConfiguration({
    CollectionId: this.dialogData.collectionId
  }).pipe(
    tap(groupListOptions => this.groupListOptions = groupListOptions),
    map((groupListOptions) => {
      return groupListOptions.map(option => {
        let caption = option.LanguageConfigurations?.find(lng => lng.LanguageId === this.userLanguage)?.Caption;
        if (caption == undefined || caption.trim() == '') {
          caption = option.LanguageConfigurations?.find(lng => lng.LanguageId === this.systemLanguage)?.Caption;
        }
        return {
          Text: caption ?? '',
          Value: option.TableFieldValueId,
          Data: option
        };
      });
    })
  );

  private fetchValues(callback?: () => void): void {
    this.collectionFieldListOptionsConfiguration$.pipe(take(1)).subscribe({
      next: (data) => {
        this.options = data;
        if (this.selected != undefined && this.selected.length > 0 && this.selected[0].Value == undefined) {
          const t = <string><unknown>this.selected[0];
          this.selected = this.options.filter(option => option.Text == t)
            .map(option => {
              return {
                Text: option.Text,
                Value: option.Value
              };
            });
        }
        this.filter('');
        if (callback) {
          callback();
        }
      }
    });
  }

  public copyCategories(): void {
    const categories = this.dialogData.records.flatMap(_ => _.Fields.filter(_ => _.ProtectedFieldType == ProtectedFieldType.ChkQuestionCategory));
    const categoryNames = categories.map(_ => _.Value?.trim());
    const uniqueCategoryNames = this.uniqueArray(categoryNames)?.filter(_ => _ != null && _.trim() != '').map(_ => _ as string);
    const toBeCreated: Array<string> = [];
    uniqueCategoryNames?.forEach(name => {
      const group = this.groupListOptions.find(c =>
        c.LanguageConfigurations?.find(l => l.Caption?.trim()?.toLowerCase() == name?.trim()?.toLowerCase()));
      if (group == undefined) {
        toBeCreated.push(name);
      }
    });
    if (toBeCreated.length > 0) {
      const fieldId = this.dialogData.records[0].Fields.find(_ => _.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkGroupCombobox)!.CollectionFieldsID;
      const createIntents$ = toBeCreated.map(_ => this.checklistApiService.addChecklistGroupValue({
        caption: _,
        collectionFieldsId: fieldId,
        languageId: this.userLanguage
      }));
      zip(createIntents$).subscribe((result) => {
        const lastValue = result[result.length - 1];
        this.store$.dispatch(formsActions.mapComboboxValues({
          formId: this.dialogData.formId,
          fieldId,
          options: lastValue,
          gridFieldId: this.dialogData.gridFieldId,
          value: undefined,
          recordId: undefined
        }));
        this.fetchValues(() => this.copyCategories());
      });
    } else {
      this.dialogData.records.forEach(r => {
        const category = r.Fields.find(_ => _.ProtectedFieldType == ProtectedFieldType.ChkQuestionCategory)?.Value;
        if (category) {
          const group = this.groupListOptions.find(c => c.LanguageConfigurations?.find(l => l.Caption?.trim()?.toLowerCase() == category?.trim()?.toLowerCase()));
          if (group) {
            this.store$.dispatch(formsActions.updateGridFormFieldValue(
              {
                formId: this.dialogData.formId,
                recordId: r.CrossLinkedInstancesID,
                gridFieldId: this.dialogData.gridFieldId,
                value: group.TableFieldValueId,
                recordFieldId: r.Fields.find(_ => _.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkGroupCombobox)!.CollectionFieldsID
              }
            ));
          }
        }
      });
      this.dialogRef.close(true);
    }

  }

  private uniqueArray(arr: Array<any>): Array<any> {
    return Array.from(new Set(arr));
  }

  public filter(term: string): void {
    this.filteredOptions = this.options.filter((option) => option.Text.toLowerCase().includes(term.toLowerCase()));
  }

  public constructor(@Inject(DIALOG_DATA) public dialogData: {
                       records: Array<Record>,
                       formId: string,
                       gridFieldId: number,
                       collectionId: number
                     },
                     public dialogRef: DialogRef,
                     private collectionListApiService: CollectionListApiService,
                     private checklistApiService: ChecklistApiService,
                     private store$: Store,
                     private actions$: Actions,
                     private destroyRef: DestroyRef
  ) {
    this.fetchValues();
    this.actions$.pipe(ofType(formsActions.createComboboxValueSucceeded), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.fetchValues();
      }
    });
    this.canCopyFromCategory = this.dialogData.records.every(r => r.Fields.find(_ => _.ProtectedFieldType == ProtectedFieldType.ChkQuestionCategory) != undefined);
  }

  public onValueChanged(item: any): void {
    // Disables multiple selection
    if (Array.isArray(this.selected) && this.selected.length > 1) {
      this.selected = this.selected.slice(-1);
    }
    if (this.selected != null && this.selected.length > 0 && this.selected[0].Value == undefined) {
      const fieldId = this.dialogData.records[0].Fields.find(_ => _.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkGroupCombobox)!.CollectionFieldsID;
      if (fieldId > 0) {
        this.store$.dispatch(formsActions.createChecklistGroupValue({
          formId: this.dialogData.formId,
          caption: item[0],
          gridFieldId: this.dialogData.gridFieldId,
          collectionFieldsId: fieldId,
          recordId: undefined
        }));
      }
    }
  }

  public onOpen(event: any): void {
    event.preventDefault();
  }


  public save(): void {
    if (this.copyFromCategory && this.canCopyFromCategory) {
      this.copyCategories();
      return;
    } else if (this.selected && this.selected.length > 0 && this.selected[0].Value > 0) {
      this.dialogData.records.forEach(r => {
        this.store$.dispatch(formsActions.updateGridFormFieldValue(
          {
            formId: this.dialogData.formId,
            recordId: r.CrossLinkedInstancesID,
            gridFieldId: this.dialogData.gridFieldId,
            value: this.selected == null ? 0 : this.selected[0].Value,
            recordFieldId: r.Fields.find(_ => _.ProtectedFieldType == ProtectedFieldType.ChkTemplateQuestionCrosslinkGroupCombobox)!.CollectionFieldsID
          }
        ));
      });
      //const
      this.dialogRef.close(this.selected != null && this.selected[0].Value);
    }
  }
}
